/**
 * Stream reducer for timeline-based bubble chat.
 * Processes SSE events incrementally into discrete bubbles.
 *
 * Type flow: SDK types (from backend) → StreamAction (internal) → Bubble (UI)
 * - SDK types: Auto-generated from claude_agent_sdk.types (see types.ts)
 * - StreamAction: Internal reducer actions (this file)
 * - Bubble: UI-specific bubble types for timeline display
 */

import type { TodoTask } from "@/components/execution/AgentTodoList";
import { getContentBlockBubbleType } from "../bubbles/block-type-mapper";
import { createBubbleFromContentBlock } from "../bubbles/factory";
import { extractBubbleMetadata } from "../bubbles/metadata-extractor";
import { normalizeToolInput } from "../bubbles/normalizer";
import type { ContentBlock } from "../tools/schemas";
import { parsePlanTasks } from "../utils/todo-utils";

type ToolCallSnapshot = {
	tool_id?: string;
	tool_name?: string;
	tool_input?: unknown;
	result?: unknown;
};

function getBlockIdentifier(block: ContentBlock): string | undefined {
	if (block.type === "tool_result" && typeof block.tool_use_id === "string") {
		return `tool_result:${block.tool_use_id}`;
	}
	if ((block as { id?: unknown }).id && typeof (block as { id?: unknown }).id === "string") {
		return (block as { id: string }).id;
	}
	return undefined;
}

/**
 * Extract content string from a content block
 */
function extractContentFromBlock(block: { type: string; [key: string]: unknown }): string {
	const blockType = block.type as string;

	switch (blockType) {
		case "text":
			return typeof block.text === "string" ? block.text : "";
		case "thinking":
		case "reasoning":
			return typeof block.thinking === "string" ? block.thinking : "";
		case "plan":
			return typeof block.plan === "string" ? block.plan : "";
		case "tool_use":
			return typeof block.input === "object" && block.input !== null
				? JSON.stringify(block.input, null, 2)
				: "";
		case "tool_result":
			return typeof block.content === "string"
				? block.content
				: typeof block.content === "object"
					? JSON.stringify(block.content, null, 2)
					: "";
		default:
			return "";
	}
}

/** Bubble types corresponding to content blocks */
export type BubbleType =
	| "user"
	| "text"
	| "tool_call"
	| "tool_result"
	| "thinking"
	| "reasoning"
	| "todo"
	| "error"
	| "sdk_error"
	| "payment_error"
	| "mcp_auth_failed"
	| "mcp_error"
	| "success"
	| "cancelled"
	| "sandbox_status"
	| "metrics"
	| "loading"
	| "compact_boundary"
	| "permission_request";

/** Bubble status */
export type BubbleStatus = "streaming" | "complete";

/** Individual bubble in the timeline */
export interface Bubble {
	id: string;
	type: BubbleType;
	status: BubbleStatus;
	content: string;
	metadata?: {
		toolName?: string;
		toolInput?: Record<string, unknown>;
		toolId?: string;
		tasks?: TodoTask[];
		sandboxId?: string;
		durationMs?: number;
		usage?: {
			input_tokens?: number;
			output_tokens?: number;
		};
		// Run metrics fields (new schema)
		model?: string;
		executionTimeMs?: number;
		inputTokensUsed?: number;
		outputTokensUsed?: number;
		claudeTokenCostUsd?: number;
		stopReason?: string;
		// SDK fields
		signature?: string | null; // From ThinkingBlock
		isError?: boolean | null; // From ToolResultBlock
		// MCP error fields
		source?: string; // MCP server name
		isAuthFailure?: boolean; // Whether this is an auth failure
		blockId?: string; // Claude block identifier (for dedupe)
		// Compact boundary fields
		compactTrigger?: "manual" | "auto";
		compactPreTokens?: number;
		// Email verification fields
		runsRemaining?: number;
		blockedTask?: string;
		// Upgrade modal fields (from 402 structured response)
		plan?: string;
		runsUsed?: number;
		runsLimit?: number;
		periodEnd?: string;
		errorCode?: string;
		// Permission request fields
		requestId?: string;
		timeoutSeconds?: number;
		resolvedStatus?: string;
		runId?: number;
		runStatus?: string;
		// Multi-turn block dedup (set by eventToAction for MESSAGE_SNAPSHOT bridge)
		blockIndex?: number;
		messageId?: string;
	};
	timestamp: Date;
}

/** Stream state */
export interface StreamState {
	bubbles: Bubble[];
	currentBubble: Bubble | null;
	currentBubbleIndex: number | null;
	toolRegistry: Map<string, Bubble>; // tool_use_id → bubble
	blockRegistry: Map<string, number>; // block_id → bubble index
	snapshotRegistry: Set<string>; // Completed block identifiers for dedupe
	runMetadata: {
		runId?: number;
		agentName?: string;
		runUuid?: string;
	};
}

/** Stream actions */
export type StreamAction =
	| { type: "START_USER_MESSAGE"; message: string }
	| {
			type: "BLOCK_START";
			blockType: string;
			blockId: string;
			metadata?: Record<string, unknown>;
	  }
	| { type: "BLOCK_DELTA"; delta: string }
	| { type: "BLOCK_STOP"; blockId?: string }
	| {
			type: "TOOL_USE";
			toolId: string;
			toolName: string;
			input: Record<string, unknown>;
	  }
	| { type: "TOOL_RESULT"; toolUseId: string; result: string; isError?: boolean | null }
	| { type: "PLAN_DELTA"; text: string }
	| { type: "THINKING"; content: string; signature?: string | null }
	| { type: "REASONING"; content: string }
	| { type: "ERROR"; message: string }
	| { type: "SDK_ERROR"; message: string }
	| {
			type: "PAYMENT_ERROR";
			message: string;
			extra?: {
				plan?: string;
				runs_used?: number;
				runs_limit?: number;
				period_end?: string;
				error_code?: string;
			};
	  }
	| { type: "MCP_AUTH_FAILED"; message: string; source?: string }
	| { type: "MCP_ERROR"; message: string; source?: string }
	| { type: "SDK_SUCCESS" }
	| { type: "CANCELLED" }
	| {
			type: "SANDBOX_CONNECTING";
			message: string;
	  }
	| {
			type: "SANDBOX_CONNECTED";
			sandboxId: string;
			durationMs?: number;
			message: string;
	  }
	| {
			type: "RUN_METRICS";
			model?: string;
			executionTimeMs?: number;
			inputTokensUsed?: number;
			outputTokensUsed?: number;
			claudeTokenCostUsd?: number;
			stopReason?: string;
	  }
	| {
			type: "METADATA";
			data: {
				runId?: number;
				agentName?: string;
				runUuid?: string;
				instanceId?: number;
				taskId?: number;
				status?: string;
			};
	  }
	| {
			type: "MESSAGE_SNAPSHOT";
			messageId?: string;
			role?: string;
			model?: string;
			usage?: Record<string, unknown>;
			toolCalls?: Array<Record<string, unknown>>;
			contentBlocks: ContentBlock[];
	  }
	| {
			type: "COMPACT_BOUNDARY";
			trigger: "manual" | "auto";
			preTokens: number;
	  }
	| {
			type: "PERMISSION_REQUEST";
			requestId: string;
			toolName: string;
			toolInput: Record<string, unknown>;
			timeoutSeconds: number;
	  }
	| { type: "PERMISSION_RESOLVED"; requestId: string; resolvedStatus: string }
	| { type: "RESET" };

/** Initial state */
export const initialStreamState: StreamState = {
	bubbles: [],
	currentBubble: null,
	currentBubbleIndex: null,
	toolRegistry: new Map(),
	blockRegistry: new Map(),
	snapshotRegistry: new Set(),
	runMetadata: {},
};

/** Helper to create and append a simple bubble */
function appendSimpleBubble(
	state: StreamState,
	type: BubbleType,
	content: string,
	metadata?: Bubble["metadata"],
	status: BubbleStatus = "complete"
): StreamState {
	const bubble: Bubble = {
		id: `${type}-${Date.now()}`,
		type,
		status,
		content,
		metadata,
		timestamp: new Date(),
	};
	return { ...state, bubbles: [...state.bubbles, bubble] };
}

/**
 * Stream reducer - processes events into timeline bubbles
 */
export function streamReducer(state: StreamState, action: StreamAction): StreamState {
	switch (action.type) {
		case "START_USER_MESSAGE": {
			const timestamp = Date.now();
			return {
				...state,
				bubbles: [
					...state.bubbles,
					{
						id: `user-${timestamp}`,
						type: "user",
						status: "complete",
						content: action.message,
						timestamp: new Date(),
					},
					{
						id: `loading-${timestamp}`,
						type: "loading",
						status: "streaming",
						content: "",
						timestamp: new Date(),
					},
				],
			};
		}

		case "BLOCK_START": {
			const { blockType, blockId, metadata } = action;
			const blockRegistry = new Map(state.blockRegistry);

			// Fix 2: Skip if block already registered (prevents duplicates on reconnect/retry)
			if (blockId && (blockRegistry.has(blockId) || state.snapshotRegistry.has(blockId))) {
				return state;
			}

			// Remove loading bubble if present (this is the first real content)
			const bubblesWithoutLoading = state.bubbles.filter((b) => b.type !== "loading");

			// For tool_use blocks, add placeholder immediately so user sees tool call during streaming
			if (blockType === "tool_use") {
				const metadataWithBlockId: Record<string, unknown> = metadata ? { ...metadata } : {};
				if (blockId) {
					metadataWithBlockId.blockId = blockId;
				}
				// Create placeholder bubble with streaming status
				const toolPlaceholder: Bubble = {
					id: blockId,
					type: "tool_call",
					status: "streaming",
					content: "",
					metadata: metadataWithBlockId,
					timestamp: new Date(),
				};

				const toolRegistry = new Map(state.toolRegistry);
				toolRegistry.set(blockId, toolPlaceholder);

				const currentBubbleIndex = bubblesWithoutLoading.length;
				const bubbles = [...bubblesWithoutLoading, toolPlaceholder];

				return {
					...state,
					bubbles,
					toolRegistry,
					currentBubble: toolPlaceholder,
					currentBubbleIndex,
					blockRegistry: blockRegistry.set(blockId, currentBubbleIndex),
				};
			}

			// Create new bubble for non-tool blocks (text, thinking, etc.)
			const metadataWithBlockId: Record<string, unknown> | undefined = blockId
				? { ...(metadata || {}), blockId }
				: metadata;
			const bubble: Bubble = {
				id: blockId,
				type: getContentBlockBubbleType(blockType),
				status: "streaming",
				content: "",
				metadata: metadataWithBlockId,
				timestamp: new Date(),
			};

			const currentBubbleIndex = bubblesWithoutLoading.length;
			const bubbles = [...bubblesWithoutLoading, bubble];

			const nextRegistry = blockId ? blockRegistry.set(blockId, currentBubbleIndex) : blockRegistry;

			return {
				...state,
				currentBubble: bubble,
				currentBubbleIndex,
				bubbles,
				toolRegistry: state.toolRegistry,
				blockRegistry: nextRegistry,
			};
		}

		case "BLOCK_DELTA": {
			// Fix 8: Warn when delta arrives for non-existent block
			if (!state.currentBubble || state.currentBubbleIndex === null) {
				console.warn("[Reducer] BLOCK_DELTA for unknown block - delta dropped:", action.delta);
				return state;
			}

			const updatedBubble: Bubble = {
				...state.currentBubble,
				content: state.currentBubble.content + action.delta,
			};

			const updatedBubbles = state.bubbles.map((bubble, index) =>
				index === state.currentBubbleIndex ? updatedBubble : bubble
			);

			// Append delta to current bubble content
			return {
				...state,
				currentBubble: updatedBubble,
				bubbles: updatedBubbles,
			};
		}

		case "BLOCK_STOP": {
			if (!state.currentBubble) {
				return state;
			}

			if (state.currentBubbleIndex === null) {
				return {
					...state,
					currentBubble: null,
				};
			}

			const completedBubble: Bubble = { ...state.currentBubble, status: "complete" };
			const updatedBubbles = state.bubbles.map((bubble, index) =>
				index === state.currentBubbleIndex ? completedBubble : bubble
			);

			const blockRegistry = new Map(state.blockRegistry);
			const snapshotRegistry = new Set(state.snapshotRegistry);
			if (action.blockId) {
				blockRegistry.delete(action.blockId);
				snapshotRegistry.add(action.blockId);
			}
			// Bridge: register MESSAGE_SNAPSHOT fallback key to prevent snapshot duplicates.
			// MESSAGE_SNAPSHOT uses "${messageId}:${blockType}:${index}" format for text blocks.
			const cb = state.currentBubble;
			if (cb?.metadata?.messageId != null && cb?.metadata?.blockIndex != null) {
				snapshotRegistry.add(`${cb.metadata.messageId}:${cb.type}:${cb.metadata.blockIndex}`);
			}
			return {
				...state,
				bubbles: updatedBubbles,
				currentBubble: null,
				currentBubbleIndex: null,
				blockRegistry,
				snapshotRegistry,
			};
		}

		case "TOOL_USE": {
			const { toolId, toolName, input } = action;

			// Fix 6: Skip if tool already processed (prevents duplicates)
			if (state.snapshotRegistry.has(toolId)) {
				return state;
			}

			// Process and normalize toolInput (e.g., ensure TodoWrite tasks have IDs)
			let toolInput: Record<string, unknown> | undefined;
			if (typeof input === "object" && input !== null) {
				toolInput = normalizeToolInput(toolName, input);
			}

			// DEBUG: Log TodoWrite actions
			if (toolName === "TodoWrite") {
				console.log("[TodoWrite DEBUG] TOOL_USE action in reducer:", {
					toolId,
					toolName,
					rawInput: input,
					normalizedInput: toolInput,
				});
			}

			// Get placeholder from registry (created by BLOCK_START)
			const placeholder = state.toolRegistry.get(toolId);

			const snapshotRegistry = new Set(state.snapshotRegistry);

			if (!placeholder) {
				// Fallback: if no placeholder exists, create new bubble (shouldn't happen with new BLOCK_START logic)
				const toolBubble: Bubble = {
					id: `tool-${toolId}`,
					type: "tool_call",
					status: "complete",
					content: typeof input === "string" ? input : JSON.stringify(input, null, 2),
					metadata: {
						toolName,
						toolId,
						toolInput,
						blockId: toolId,
					},
					timestamp: new Date(),
				};

				const toolRegistry = new Map(state.toolRegistry);
				toolRegistry.set(toolId, toolBubble);

				snapshotRegistry.add(toolId);

				return {
					...state,
					bubbles: [...state.bubbles, toolBubble],
					toolRegistry,
					currentBubble: null,
					currentBubbleIndex: null,
					snapshotRegistry,
				};
			}

			// Update existing placeholder with complete metadata (BLOCK_START already added it to bubbles)
			const toolBubble: Bubble = {
				...placeholder,
				status: "complete",
				content: typeof input === "string" ? input : JSON.stringify(input, null, 2),
				metadata: {
					...placeholder.metadata,
					toolName,
					toolId,
					toolInput,
					blockId: toolId,
				},
			};

			// Find and update the placeholder in bubbles array
			const bubbleIndex = state.bubbles.findIndex((b) => b.id === placeholder.id);
			const updatedBubbles = [...state.bubbles];
			if (bubbleIndex !== -1) {
				updatedBubbles[bubbleIndex] = toolBubble;
			} else {
				// Fallback: append if not found (shouldn't happen)
				updatedBubbles.push(toolBubble);
			}

			const toolRegistry = new Map(state.toolRegistry);
			toolRegistry.set(toolId, toolBubble);

			snapshotRegistry.add(toolId);

			return {
				...state,
				bubbles: updatedBubbles,
				toolRegistry,
				snapshotRegistry,
				currentBubble: null,
				currentBubbleIndex: null,
			};
		}

		case "TOOL_RESULT": {
			const { toolUseId, result } = action;
			const dedupeKey = `tool_result:${toolUseId}`;

			// Skip if already processed via MESSAGE_SNAPSHOT
			if (state.snapshotRegistry.has(dedupeKey)) {
				return state;
			}

			// Create new bubble for tool result
			const resultBubble: Bubble = {
				id: `result-${toolUseId}`,
				type: "tool_result",
				status: "complete",
				content: result,
				metadata: {
					...extractBubbleMetadata({ streamAction: action }),
					toolId: toolUseId,
					toolName: state.toolRegistry.get(toolUseId)?.metadata?.toolName,
					toolInput: state.toolRegistry.get(toolUseId)?.metadata?.toolInput,
					blockId: toolUseId,
				},
				timestamp: new Date(),
			};

			const snapshotRegistry = new Set(state.snapshotRegistry);
			snapshotRegistry.add(toolUseId);

			return {
				...state,
				bubbles: [...state.bubbles, resultBubble],
				snapshotRegistry,
			};
		}

		case "PLAN_DELTA": {
			const tasks = parsePlanTasks(action.text);

			// Create new todo bubble
			const todoBubble: Bubble = {
				id: `todo-${Date.now()}`,
				type: "todo",
				status: "complete",
				content: action.text,
				metadata: { tasks },
				timestamp: new Date(),
			};

			return {
				...state,
				bubbles: [...state.bubbles, todoBubble],
			};
		}

		case "THINKING": {
			const thinkingBubble: Bubble = {
				id: `thinking-${Date.now()}`,
				type: "thinking",
				status: "complete",
				content: action.content,
				metadata: extractBubbleMetadata({ streamAction: action }),
				timestamp: new Date(),
			};

			return {
				...state,
				bubbles: [...state.bubbles, thinkingBubble],
			};
		}

		case "REASONING": {
			const reasoningBubble: Bubble = {
				id: `reasoning-${Date.now()}`,
				type: "reasoning",
				status: "complete",
				content: action.content,
				timestamp: new Date(),
			};

			return {
				...state,
				bubbles: [...state.bubbles, reasoningBubble],
			};
		}

		case "ERROR":
			return appendSimpleBubble(state, "error", action.message);

		case "SDK_ERROR":
			return appendSimpleBubble(state, "sdk_error", action.message);

		case "PAYMENT_ERROR":
			return appendSimpleBubble(state, "payment_error", action.message, {
				plan: action.extra?.plan,
				runsUsed: action.extra?.runs_used,
				runsLimit: action.extra?.runs_limit,
				periodEnd: action.extra?.period_end,
				errorCode: action.extra?.error_code,
			});

		case "MCP_AUTH_FAILED":
			return appendSimpleBubble(state, "mcp_auth_failed", action.message, {
				source: action.source,
				isAuthFailure: true,
			});

		case "MCP_ERROR":
			return appendSimpleBubble(state, "mcp_error", action.message, {
				source: action.source,
				isAuthFailure: false,
			});

		case "SDK_SUCCESS":
			return appendSimpleBubble(state, "success", "Task completed successfully");

		case "CANCELLED":
			return appendSimpleBubble(state, "cancelled", "Execution stopped");

		case "SANDBOX_CONNECTING":
			return appendSimpleBubble(state, "sandbox_status", action.message, undefined, "streaming");

		case "SANDBOX_CONNECTED": {
			// Update the last sandbox_status bubble if it's still streaming
			const lastBubble = state.bubbles[state.bubbles.length - 1];
			if (lastBubble && lastBubble.type === "sandbox_status" && lastBubble.status === "streaming") {
				const updatedBubbles = [...state.bubbles];
				updatedBubbles[updatedBubbles.length - 1] = {
					...lastBubble,
					status: "complete",
					content: action.message,
					metadata: extractBubbleMetadata({ streamAction: action }),
				};

				return {
					...state,
					bubbles: updatedBubbles,
				};
			}

			// Otherwise create a new bubble
			const connectedBubble: Bubble = {
				id: `sandbox-connected-${Date.now()}`,
				type: "sandbox_status",
				status: "complete",
				content: action.message,
				metadata: extractBubbleMetadata({ streamAction: action }),
				timestamp: new Date(),
			};

			return {
				...state,
				bubbles: [...state.bubbles, connectedBubble],
			};
		}

		case "RUN_METRICS": {
			const metricsBubble: Bubble = {
				id: `metrics-${Date.now()}`,
				type: "metrics",
				status: "complete",
				content: "Execution metrics",
				metadata: {
					model: action.model,
					executionTimeMs: action.executionTimeMs,
					inputTokensUsed: action.inputTokensUsed,
					outputTokensUsed: action.outputTokensUsed,
					claudeTokenCostUsd: action.claudeTokenCostUsd,
					stopReason: action.stopReason,
				},
				timestamp: new Date(),
			};

			return {
				...state,
				bubbles: [...state.bubbles, metricsBubble],
			};
		}

		case "METADATA": {
			return {
				...state,
				runMetadata: {
					...state.runMetadata,
					...action.data,
				},
			};
		}

		case "MESSAGE_SNAPSHOT": {
			const snapshotRegistry = new Set(state.snapshotRegistry);
			const messageId = action.messageId || `snapshot-${Date.now()}`;
			const baseTimestamp = new Date();
			const appendedBubbles: Bubble[] = [];
			const updatedBubbles = [...state.bubbles];
			const toolCallLookup = new Map<string, ToolCallSnapshot>();

			if (Array.isArray(action.toolCalls)) {
				for (const entry of action.toolCalls) {
					const candidate = entry as ToolCallSnapshot;
					if (candidate?.tool_id && typeof candidate.tool_id === "string") {
						toolCallLookup.set(candidate.tool_id, candidate);
					}
				}
			}

			for (let index = 0; index < action.contentBlocks.length; index += 1) {
				const block = action.contentBlocks[index];
				const rawBlockId = getBlockIdentifier(block);
				// Fix 3: Use message_id + block type + index for deduplication key instead of content
				const fallbackId = rawBlockId || `${messageId}:${block.type}:${index}`;

				// Check if block ID already processed (works for tool_use, tool_result with IDs)
				if (rawBlockId && snapshotRegistry.has(rawBlockId)) {
					continue;
				}

				// Fix 3: For blocks without IDs, check if this position was already processed
				if (!rawBlockId && snapshotRegistry.has(fallbackId)) {
					continue;
				}

				// Fix 4: Check for existing streaming bubble by message-scoped position.
				// Uses messageId + blockIndex to avoid cross-turn overwrites (block-N IDs are unique per session).
				const existingStreamingIdx = updatedBubbles.findIndex(
					(b) => b.metadata?.messageId === messageId && b.metadata?.blockIndex === index
				);

				if (existingStreamingIdx !== -1) {
					// Update existing streaming bubble to complete status instead of creating duplicate
					const existingBubble = updatedBubbles[existingStreamingIdx];
					const content = extractContentFromBlock(block);
					updatedBubbles[existingStreamingIdx] = {
						...existingBubble,
						status: "complete" as const,
						content: content || existingBubble.content,
					};
					snapshotRegistry.add(existingBubble.id);
					continue;
				}

				const toolCallMeta =
					block.type === "tool_result" && typeof block.tool_use_id === "string"
						? toolCallLookup.get(block.tool_use_id)
						: undefined;

				const bubble = createBubbleFromContentBlock(
					block,
					{
						messageId,
						baseTimestamp,
						blockIndex: index,
					},
					toolCallMeta ? { toolCallMeta } : undefined
				);

				if (!bubble) {
					continue;
				}

				const finalBlockId = bubble.metadata?.blockId || fallbackId;

				// For blocks without IDs (text blocks), check if bubble with same blockId exists
				// This uses position-based deduplication instead of content matching (Fix 3)
				if (!rawBlockId) {
					const existsInBubbles = updatedBubbles.some((b) => b.metadata?.blockId === finalBlockId);
					const existsInAppended = appendedBubbles.some(
						(b) => b.metadata?.blockId === finalBlockId
					);

					if (existsInBubbles || existsInAppended) {
						snapshotRegistry.add(finalBlockId);
						continue;
					}
				}

				snapshotRegistry.add(finalBlockId);
				appendedBubbles.push(bubble);
			}

			if (appendedBubbles.length === 0 && updatedBubbles === state.bubbles) {
				return state;
			}

			return {
				...state,
				bubbles: [...updatedBubbles, ...appendedBubbles],
				snapshotRegistry,
			};
		}

		case "COMPACT_BOUNDARY":
			return appendSimpleBubble(state, "compact_boundary", "Conversation summarized", {
				compactTrigger: action.trigger,
				compactPreTokens: action.preTokens,
			});

		case "PERMISSION_REQUEST": {
			const permissionId = `permission-${action.requestId}`;
			if (
				state.bubbles.some((b) => b.id === permissionId) ||
				state.snapshotRegistry.has(permissionId)
			) {
				return state;
			}
			const snapshotRegistry = new Set(state.snapshotRegistry);
			snapshotRegistry.add(permissionId);
			const permissionBubble: Bubble = {
				id: permissionId,
				type: "permission_request",
				status: "streaming",
				content: `Tool "${action.toolName}" requires approval`,
				metadata: {
					requestId: action.requestId,
					toolName: action.toolName,
					toolInput: action.toolInput,
					timeoutSeconds: action.timeoutSeconds,
					runId: state.runMetadata.runId,
				},
				timestamp: new Date(),
			};
			return {
				...state,
				bubbles: [...state.bubbles.filter((b) => b.type !== "loading"), permissionBubble],
				snapshotRegistry,
			};
		}

		case "PERMISSION_RESOLVED": {
			const targetId = `permission-${action.requestId}`;
			const idx = state.bubbles.findIndex((b) => b.id === targetId);
			if (idx === -1) return state;
			const updated = [...state.bubbles];
			updated[idx] = {
				...updated[idx],
				status: "complete",
				metadata: { ...updated[idx].metadata, resolvedStatus: action.resolvedStatus },
			};
			return { ...state, bubbles: updated };
		}

		case "RESET":
			return {
				...initialStreamState,
				toolRegistry: new Map(),
				blockRegistry: new Map(),
				snapshotRegistry: new Set(),
			};

		default:
			return state;
	}
}
